import React from 'react';

// Written from what the app actually does (see src/index-worker.js, src/queries.js,
// src/config.js APP_SCOPES). If data handling changes, change this page with it —
// Slack Marketplace reviewers check the policy against the app's behaviour.
const UPDATED = '15 September 2026';
const CONTACT = 'decisiontrackerbot@gmail.com';

const Section = ({ title, children }) => (
  <section className="pp-section">
    <h2>{title}</h2>
    {children}
  </section>
);

const Privacy = () => (
  <div className="pp">
    <div className="pp-wrap">
      <h1>Privacy Policy</h1>
      <p className="pp-updated">Last updated {UPDATED}</p>

      <p className="pp-lede">
        Decision Tracker is a Slack app that helps teams record decisions and find them again later.
        This policy explains what data the app and this website collect, why, where it goes, how long
        it is kept, and how to have it deleted. Questions: <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.
      </p>

      <nav className="pp-toc" aria-label="Contents">
        <a href="#collect">What we collect</a>
        <a href="#messages">Slack messages</a>
        <a href="#notion">Notion</a>
        <a href="#use">How we use data</a>
        <a href="#processors">Who processes it</a>
        <a href="#retention">Retention &amp; deletion</a>
        <a href="#rights">Your rights</a>
      </nav>

      <Section title="Who we are">
        <p>
          Decision Tracker (“we”, “us”) provides the Decision Tracker Slack app and the website at
          decisiontracker.app. When your organisation installs the app, your organisation is the
          controller of the workspace data and we process it on its behalf to provide the service.
        </p>
        <p>
          We handle personal information in accordance with Australia’s <i>Privacy Act 1988</i> (Cth)
          and the Australian Privacy Principles, and, where it applies, the EU General Data Protection
          Regulation (GDPR).
        </p>
        <p>
          The party responsible for data processing is Decision Tracker, contactable at{' '}
          <a href={`mailto:${CONTACT}`}>{CONTACT}</a>. Contact us for our registered business name and details.
        </p>
      </Section>

      <Section title="What we collect">
        <div id="collect" />
        <h3>1. Workspace and installation data</h3>
        <p>
          When the app is installed we receive and store: your Slack workspace ID and name, the Slack
          user ID of the person who installed it, the OAuth access and refresh tokens Slack issues to
          the app, the permissions (scopes) granted, and the installation date.
        </p>

        <h3>2. Decisions you create</h3>
        <p>
          When someone in your workspace tracks a decision we store what they enter: the title,
          description, deadline, expected and actual outcome, the Slack user IDs of the creator and of
          any approvers, approval status and timestamps. If the decision is linked to a Slack message we
          store a pointer to it — the channel ID, message timestamp and permalink — not a copy of the
          conversation.
        </p>

        <h3>3. Slack profile information</h3>
        <p>
          We look up display names and profile photos of workspace members through Slack so decisions,
          approvals and digests can show who is involved. We do not store these profiles. Where Slack
          provides the email address of the person who installed the app, we use it to send account,
          trial and billing emails.
        </p>

        <h3>4. Product usage</h3>
        <p>
          We record which features are used — for example opening the Home tab, creating a decision or
          exporting a report — together with the Slack workspace ID, Slack user ID and time. We use this
          to understand and improve the product.
        </p>

        <h3>5. Billing</h3>
        <p>
          Payments are handled by Stripe. We never see or store your card details; we store only the
          Stripe customer identifier linked to your workspace.
        </p>

        <h3>6. This website</h3>
        <p>
          decisiontracker.app uses Google Analytics to measure visits and Tawk.to to provide live chat.
          These services may set cookies and collect information such as pages viewed, browser and
          approximate location. If you contact us through chat or email we keep that correspondence.
        </p>
        <p>
          When you click <b>Connect Slack</b> in our Notion template, we set a first-party cookie for up to
          seven days that only records that you came from the template, so that after installing the Slack
          app you are taken on to connect Notion. It contains no personal information.
        </p>
      </Section>

      <Section title="Slack messages">
        <div id="messages" />
        <p>
          To suggest tracking a decision, and to answer questions like “why did we decide this?”, the app
          reads new messages in <b>public channels it has been added to</b>. It adds itself to a public
          channel only when a member of that channel uses the app there. It does not read private
          channels or direct messages.
        </p>
        <p>
          Message content is processed as it arrives to decide whether to respond, and is <b>not stored</b>.
          The only message text we keep is what becomes part of a decision a person chooses to save
          (for example, a title pre-filled from their message). When a decision is opened, the linked
          conversation is fetched live from Slack — so if a message is deleted in Slack, it is no longer
          shown by Decision Tracker.
        </p>
        <p>
          Suggestions and recall replies are sent as ephemeral messages, visible only to the person they
          are for. We do not use message content for advertising, we do not sell it, and we do not use it
          to train machine learning models.
        </p>
      </Section>

      <Section title="Notion (optional)">
        <div id="notion" />
        <p>
          If someone in your workspace connects Notion, Decision Tracker copies the decisions your team tracks
          into the Notion page they choose. This is optional and only happens after they approve it on
          Notion’s own permission screen, where they pick which pages Decision Tracker can use.
        </p>
        <h3>What we store</h3>
        <p>
          The access token Notion issues to the app, your Notion workspace ID and name, the ID of the
          person who connected it, and the IDs and link of the decision log database we write to. We also
          keep a short queue of decision IDs waiting to be copied if Notion is temporarily unavailable.
        </p>
        <h3>What we send to Notion</h3>
        <p>
          For each tracked decision: its title, reasoning, status, deadline, decided date, outcome, the
          display names of its owner and approvers, the name of the <b>public</b> channel it came from and a
          link back to the Slack thread. We never send private channel names, message text that nobody
          chose to track, or anything else from Slack. Once copied, that content is stored by Notion under
          your Notion workspace’s terms.
        </p>
        <h3>What we read in Notion</h3>
        <p>
          Only the pages you share with the app, and only to find the decision log in them, add any columns
          it is missing, and keep its rows up to date. We don’t read or keep any other Notion content. We
          update the properties of rows we created and never overwrite what your team writes in a
          decision’s page.
        </p>
        <h3>Disconnecting</h3>
        <p>
          Choose <b>Disconnect Notion</b> from the app’s Home tab menu. We revoke the Notion token and delete
          the connection record straight away. The decision log stays in your Notion workspace, and you can
          also remove Decision Tracker’s access from Notion’s settings at any time.
        </p>
      </Section>

      <Section title="How we use data">
        <div id="use" />
        <ul>
          <li>To provide the app: saving decisions, reminders, approvals, digests, search and exports.</li>
          <li>To send service messages in Slack, such as deadline reminders and the weekly digest.</li>
          <li>To send account, trial and billing emails to the person who installed the app.</li>
          <li>To take payment and manage subscriptions.</li>
          <li>To keep the service secure, diagnose problems and improve the product.</li>
        </ul>
        <p>We do not sell personal data, and we do not share it with anyone except the processors below.</p>
      </Section>

      <Section title="Who processes it">
        <div id="processors" />
        <ul>
          <li><b>Cloudflare</b> — hosting and data storage (Workers and Workers KV). Request logs, which can include the content of Slack events, are retained by Cloudflare for up to 7 days.</li>
          <li><b>Slack</b> — the platform the app runs on.</li>
          <li><b>Notion</b> — only if your team connects it, to hold the decision log you asked us to keep in sync.</li>
          <li><b>Stripe</b> — payment processing.</li>
          <li><b>Mailchimp</b> — account, trial and product emails to the installing user.</li>
          <li><b>Google Analytics</b> — website analytics.</li>
          <li><b>Tawk.to</b> — website live chat.</li>
        </ul>
        <p>
          These providers may process data outside Australia and outside your country, including in the
          United States. Before disclosing personal information overseas we take reasonable steps to
          ensure it is protected consistently with the Australian Privacy Principles, and for EU data we
          rely on the providers’ standard contractual clauses.
        </p>
      </Section>

      <Section title="Retention and deletion">
        <div id="retention" />
        <ul>
          <li><b>Uninstalling</b> the app from Slack revokes its access tokens immediately and deletes your workspace’s product usage records.</li>
          <li><b>Decisions</b> are kept after uninstalling so they are not lost if you reinstall. To have them permanently deleted, email <a href={`mailto:${CONTACT}`}>{CONTACT}</a> from a workspace admin account and we will delete all data for your workspace within 30 days.</li>
          <li><b>Export and share links</b> are signed and expire — CSV export links after 1 hour.</li>
          <li><b>Message content</b> is not retained, beyond Cloudflare’s short-lived request logs described above.</li>
        </ul>
        <p>You can export every decision to CSV from the app’s Home tab at any time, including before deleting.</p>
      </Section>

      <Section title="Security">
        <p>
          All traffic uses HTTPS. Requests from Slack are verified with Slack’s request signing. Secrets
          are stored encrypted in Cloudflare, report and export links are signed, and access to production
          data is limited to the people who operate the service.
        </p>
      </Section>

      <Section title="Your rights">
        <div id="rights" />
        <p>
          Depending on where you live you may have the right to access, correct, export or delete your
          personal data, to restrict or object to processing, to withdraw consent, and to data
          portability. Workspace data is controlled by your organisation, so we may need to confirm
          requests with a workspace admin. To make a request, email <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.
        </p>
        <p>
          Under the Australian Privacy Principles you can request access to, and correction of, the
          personal information we hold about you. If you believe we have mishandled your information,
          please contact us first so we can try to resolve it. If you are not satisfied, you can complain
          to the Office of the Australian Information Commissioner (oaic.gov.au), or, in the EU, to your
          local data protection supervisory authority.
        </p>
        <p>
          These rights can be limited — for example where fulfilling a request would reveal personal
          information about another person, or where we must keep information to meet a legal obligation.
          We will tell you if that applies.
        </p>
      </Section>

      <Section title="Children">
        <p>Decision Tracker is a workplace tool and is not directed to anyone under 16.</p>
      </Section>

      <Section title="Changes">
        <p>
          If we change how we handle data we will update this page and the date above. For material
          changes we will notify the person who installed the app.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          Decision Tracker — <a href={`mailto:${CONTACT}`}>{CONTACT}</a>
        </p>
      </Section>
    </div>
  </div>
);

export default Privacy;



// WEBPACK FOOTER //
// ./src/components/Privacy.js